import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import AdminAppBar from "../../../adminComponents/AdminAppBar";
import CustomHeading from "../../../common/CustomHeading";
import CustomSearch from "../../../common/CustomSearch";
import CustomTable from "../../../common/CustomTable";
import EstimateFilter from "./EstimateFilter";
import { MdFilterListAlt } from "react-icons/md";
import { BsEyeFill } from "react-icons/bs";
import EstimateService from "../../../Services/EstimateService";
import CustomModel from "../../../common/CustomModel";
import { showInfoToast } from "../../../Constant/Notification";

const EstimateList = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState([]);
  const [cachedData, setCachedData] = useState({});
  const [pagination, setPagination] = useState({
    next: null,
    prev: null,
    currentDirection: null,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [visible, setVisible] = useState(false);

  const generateCacheKey = (pageNumber) => {
    return `${searchQuery}-${pageNumber}-${pageSize}-${startDate?.toISOString() || ""}-${endDate?.toISOString() || ""}`;
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const fetchData = useCallback(
    async (direction = null, searchKey = searchQuery, pageToken = null, targetPage = currentPage) => {
      setLoading(true);
      try {
        const cacheKey = generateCacheKey(targetPage);
        
        console.log("Making API call for page:", targetPage);
        const body = {
          limit: pageSize,
          searchKey,
          next: direction === "next" ? pageToken : null,
          prev: direction === "previous" ? pageToken : null,
          direction,
          startDate: startDate || "",
          endDate: endDate || "",
        };
  
        const response = await EstimateService.getEstimateList(body);
        const estimates = response.data?.estimates || [];
  
       
        setCachedData((prev) => ({
          ...prev,
          [cacheKey]: {
            data: estimates,
            next: response.data?.next,
            prev: response.data?.prev,
          },
        }));
  
        setData(estimates);
        setPagination({
          next: response.data?.next,
          prev: response.data?.prev,
          currentDirection: direction,
        });
  
        if (searchKey !== searchQuery) {
          setSearchQuery(searchKey);
        }
      } catch (error) {
        console.error("Error fetching estimates:", error);
      } finally {
        setLoading(false);
      }
    },
    [endDate, pageSize, searchQuery, startDate, currentPage]
  );

  useEffect(() => {
   
    setCurrentPage(1);
    setCachedData({});
    fetchData();
  }, [pageSize, startDate, endDate]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setCachedData({});
    fetchData(null, searchQuery);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleEditClick = useCallback((record) => {
    setSelectedEstimate(record?.estimatePdf);
    setVisible(true);
  }, []);

  const handleFilter = () => {
    setIsOpen(true);
  };

  const handleApplyFilter = useCallback(() => {
    if (!startDate || !endDate) {
      showInfoToast("Please select both start and end dates.");
      return;
    }
    setIsOpen(false);
  }, [startDate, endDate]);

  const clearFilters = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
    setCachedData({});
    fetchData();
    setIsOpen(false);
  }, []);

  const handlePageChange = (token, direction) => {
    const newPage = direction === "next" ? currentPage + 1 : currentPage - 1;
    const cacheKey = generateCacheKey(newPage);
  
    if (direction === "next") {
      // For next page, always fetch new data
      setCurrentPage(newPage);
      fetchData("next", searchQuery, token, newPage);
    } else {
      if (cachedData[cacheKey]) {
        setCurrentPage(newPage);
        setData(cachedData[cacheKey].data);
        setPagination({
          next: cachedData[cacheKey].next,
          prev: cachedData[cacheKey].prev,
          currentDirection: direction,
        });
      }
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
  };

  const columns = [
    {
      key: "estimateId",
      title: "Estimate ID",
      render: (text) => <div className="text-center">{text}</div>,
    },
    {
      key: "leadName",
      title: "Lead Name",
      render: (text) => <div className="text-center">{text}</div>,
    },
    {
      key: "generatedBy",
      title: "Created By",
      render: (text) => <div className="text-center">{text}</div>,
    },
    {
      key: "propertyName",
      title: "Property",
      render: (text) => <div className="text-center">{text}</div>,
    },
    {
      key: "unitNo",
      title: "Unit No",
      render: (text) => <div className="text-center">{text}</div>,
    },
    {
      key: "estimatePdf",
      title: "Action",
      render: (_, record) => (
        <div
          onClick={() => handleEditClick(record)}
          className="flex justify-center items-center cursor-pointer"
        >
          <BsEyeFill className="text-blue-500 text-lg" />
        </div>
      ),
    },
  ];

  return (
    <div>
      <AdminAppBar />
      <ToastContainer />
      <div className="pt-28 px-6">
        <div className="flex items-center justify-between">
          <CustomHeading title="Estimates" isIcon={true} />
          <div className="flex items-center gap-4">
            <CustomSearch
              placeholder="Search Property"
              searchTerm={searchQuery}
              handleSearchSubmit={handleSearchSubmit}
              handleSearchChange={handleSearchChange}
            />
            <button
              onClick={handleFilter}
              className="bg-white justify-between text-base font-medium w-[131px] h-[50px] rounded-[10px] flex items-center gap-3 text-customGreen py-2 px-4 hover:bg-appGreenHover transition duration-300"
            >
              Filters
              <MdFilterListAlt />
            </button>
          </div>
        </div>

        <div className="w-full mt-12">
          <CustomTable
            columns={columns}
            data={data}
            isPagination={true}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            onPageChange={handlePageChange}
            currentPage={currentPage}
            nextId={pagination.next}
            prevId={pagination.prev}
            hasMore={!!pagination.next}
            loading={loading}
          />
        </div>

        <EstimateFilter
          visible={isOpen}
          onHide={() => setIsOpen(false)}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          handleApplyFilter={handleApplyFilter}
          clearFilters={clearFilters}
        />

        <CustomModel
          isOpen={visible}
          title="Estimate Document"
          isClose={() => setVisible(false)}
          width="w-[800px]"
          content={
            selectedEstimate ? (
              <div className="overflow-x-auto relative h-[550px]">
                {selectedEstimate ? (
                   <div dangerouslySetInnerHTML={{ __html: selectedEstimate }} />
                ) : (
                  <p>No PDF available for this estimate.</p>
                )}
              </div>
            ) : (
              <p>Loading...</p>
            )
          }
        />
      </div>
    </div>
  );
};

export default EstimateList;